import React, {useEffect, useState} from "react";
import "./MateClient.css";
import Button from "react-bootstrap/Button";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faSpinner, faFolderTree, faCircle, faBug, faMobileScreenButton, faBolt, faMicroscope,
} from "@fortawesome/free-solid-svg-icons";
import {faBluetoothB} from "@fortawesome/free-brands-svg-icons";
import BondTree from "components/BondTree";
import {Accordion, ModalTitle} from "react-bootstrap";
import {faComments} from "@fortawesome/free-regular-svg-icons";
import CustomRequest from "../../CustomRequests/CustomRequest";
import {faRotateRight} from "@fortawesome/free-solid-svg-icons/faRotateRight";
import MateChannel from "./MateChannel";
import Form from "react-bootstrap/Form";
import MateFCC from "./MateFCC";


window._checkConnectionInterval = window._checkConnectionInterval || {};
window._mateClients = window._mateClients || {};

function MateClient({bond, mate}) {
    const [isConnecting, setIsConnecting] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionProblem, setConnectionProblem] = useState(false);
    const [debugMode, setDebugMode] = useState(false);

    const [mateInfo, setMateInfo] = useState(null);
    const [mateLogs, setMateLogs] = useState([]);
    const [channelIds, setChannelIds] = useState([]);


    useEffect(() => {
        console.log("Is connecting:", isConnecting);
    }, [isConnecting]);

    useEffect(() => {
        if (isConnected && !window._checkConnectionInterval[mate.id]) {
            window._checkConnectionInterval[mate.id] = setInterval(async () => {
                const hasProblem = !(mate && mate.isDeviceConnected())
                setConnectionProblem(hasProblem);
            }, 2000);
        }
    }, [mate, isConnected])

    useEffect(() => {
        mate.setConnectionCallback(async (connected) => {
            if (connected) {
                setConnectionProblem(null);
                await updateMateInfo();
                await updateChannelIds();
                setIsConnected(true)
            }
        });
        mate.setConnectionStartedCallback(async (_isConnecting) => {
            setIsConnecting(_isConnecting)
        });
        mate.setOnLogMessage((message) => {
            let timestamp = new Date().toLocaleTimeString();
            setMateLogs(prevLogs => [`[${timestamp}]\t${message}`, ...prevLogs]);
        });

        if (!window._mateClients[mate.id]) {
            window._mateClients[mate.id] = mate;
        }

    }, [mate]);

    const updateMateInfo = async () => {
        mate.get("sys/version").then(info => {
            if (info && info.bondid) {
                setMateInfo({...info});
            } else {
                setTimeout(updateMateInfo, 100);
            }
        }).catch(error => {
            console.error('❌ Error fetching Mate info:', error);
        });
    }
    const updateChannelIds = async () => {
        let _channelIds = [];
        mate.get("channels").then(channels => {
            if (channels && Object.keys(channels).length > 0) {
                for (let c of Object.keys(channels)) {
                    if (c.toString().indexOf("_") !== 0) {
                        _channelIds.push(c);
                    }
                }
                _channelIds.sort((a, b) => parseInt(a) - parseInt(b));
                setChannelIds([].concat(_channelIds));
            } else {
                setTimeout(updateChannelIds, 100);
            }
        }).catch(error => {
            console.error('❌ Error fetching Mate info:', error);
        });
    }

    const connectToDevice = async () => {
        setIsConnecting(true);
        try {
            await mate.connect();
        } catch (error) {
            console.error('❌ Error connecting to device:', error);
        } finally {
            setIsConnecting(false);
        }
    };

    const disconnectDevice = async () => {
        if (mate && mate.isConnected) {
            try {
                await mate.disconnect();
            } catch (error) {
                console.error('❌ Error disconnecting:', error);
            }
        }
        setIsConnected(false);
        setMateInfo(null);
        setConnectionProblem(null)
        if (window._checkConnectionInterval[mate.id]) clearInterval(window._checkConnectionInterval[mate.id]);
        window._checkConnectionInterval[mate.id] = null;
    };

    const toggleDebugMode = () => {
        const newDebugMode = !debugMode;
        setDebugMode(newDebugMode);
        if (mate) {
            mate.setDebug(newDebugMode);
        }
    };

    return (<>
        <ModalTitle className="">
            {isConnected ? <span className={"me-2"}>
                        <FontAwesomeIcon icon={faCircle}
                                         size={"md"}
                                         className={"me-2 " + (connectionProblem ? "fa-fade text-warning" : "fa-beat text-success")}/>
                {mate.bondId}
                    </span> : <span className={"me-2"}>
                        <FontAwesomeIcon icon={faCircle}
                                         size={"md"}
                                         className={"me-2 text-primary"}
                                         color={""}/>
                {mate.bondId}
                    </span>}

            {isConnected && <>
                <Button
                    variant="outline-danger"
                    onClick={disconnectDevice}
                    className="me-2"
                >
                    <FontAwesomeIcon icon={faBluetoothB} className="me-2"/>
                    Disconnect
                </Button>
                {connectionProblem && <Button
                    variant="outline-primary"
                    onClick={connectToDevice}
                    className="me-2"
                >
                    <FontAwesomeIcon
                        icon={isConnecting ? faSpinner : faRotateRight}
                        spin={isConnecting}
                        className="me-2"
                    />
                    {isConnecting ? 'Connecting...' : 'Reconnect'}
                </Button>}

            </>}

            {!isConnected && mate.bondId && <Button
                variant="primary"
                onClick={connectToDevice}
                disabled={isConnecting}
                className="me-2"
            >
                <FontAwesomeIcon
                    icon={isConnecting ? faSpinner : faBolt}
                    spin={isConnecting}
                    className=""
                />
            </Button>}


            <Button
                variant={debugMode ? 'secondary' : 'outline-secondary'}
                onClick={toggleDebugMode}
                size="sm"
            ><FontAwesomeIcon icon={faBug} className=""/></Button>


            <Form.Control as="textarea"
                          hidden={!debugMode}
                          className={"mt-2 mateLogsTextarea"}
                          type="text"
                          value={mateLogs.join("\n")}
                          readOnly={true}
            />

        </ModalTitle>


        {(isConnected && mateInfo) && (<>
            <div className="card mb-3 mateInfoCard">
                <div className="card-body">
                    <p className="mb-1"><strong>ID:</strong> {mateInfo.bondid}</p>
                    <p className="mb-1"><strong>Firmware:</strong> {mateInfo.fw_ver} ({mateInfo.fw_date})
                    </p>
                </div>
            </div>
        </>)}

        {(isConnected && mateInfo) && (<>
            <Accordion id="mateTreeAccordion" className="mt-3" alwaysOpen={true} defaultActiveKey={"0"}>

                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <FontAwesomeIcon icon={faMobileScreenButton} size="xl" className="iconAccordionHeader"/>Channels
                        ({channelIds.length})</Accordion.Header>
                    <Accordion.Body>
                        <div className={"mateChannelCardGroup g-4"}>
                            {channelIds && channelIds.map((channelId, index) => <MateChannel bond={bond} mate={mate}
                                                                                             channelId={channelId}
                                                                                             key={index}/>)}
                        </div>
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="2">
                    <Accordion.Header>
                        <FontAwesomeIcon icon={faComments} size="xl" className="iconAccordionHeader"/>Custom
                        Request</Accordion.Header>
                    <Accordion.Body>
                        <CustomRequest bondApi={mate}/>
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="fcc">
                    <Accordion.Header>
                        <FontAwesomeIcon icon={faMicroscope} size="xl"
                                         className="iconAccordionHeader"/>FCC</Accordion.Header>
                    <Accordion.Body>
                        <MateFCC mate={mate}/>
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="1">
                    <Accordion.Header>
                        <FontAwesomeIcon icon={faFolderTree} size="xl" className="iconAccordionHeader"/>Mate
                        Tree</Accordion.Header>
                    <Accordion.Body>
                        <BondTree bond={mate}/>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </>)}
    </>);
}

export default MateClient;

